import { observer } from 'mobx-react-lite'
import { useParams } from 'react-router-dom'
import {
  CalendarDropdown,
  FullPageWrapper,
  Typography,
  SpinnerLoader,
  Breadcrumbs,
  type ActionItem,
} from 'shared/ui'
import { NoResultsFound } from 'shared/ui/NoResultsFound/NoResultsFound'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { AnalyticsHeader, TrendLabel, AnalyticsEmptyState } from 'pages/analytics/ui'
import { ChartFilter } from 'pages/analytics/ui/ChartFilter'
import { AnalyticsPath } from 'pages/analytics/route/analyticsPath'
import { copySnippet } from 'pages/analytics/pages/conversions/ui/ConversionActions/conversionActions'
import {
  ConversionDetailsProvider,
  useConversionDetailsContext,
} from '../context/conversionDetailsContext'
import { ConversionDetailsLineChart, ConversionDetailsBarChart } from './Chart'
import styles from './styles.module.scss'

const ConversionDetailsPageContent = observer(() => {
  const {
    calendarFilterStore,
    chartFilterStore,
    chartDataStore,
    conversionDetail,
    pageLoading,
    chartLoading,
    openExportModal,
  } = useConversionDetailsContext()
  const { getStatisticByKey, hasStatistic } = chartDataStore
  const entities = useEntitiesContext()

  const conversionValueStat = getStatisticByKey('conversion_value')
  const totalStat = getStatisticByKey('total')
  const conversionRateStat = getStatisticByKey('conversion_rate')

  const conversionDetailsActions: ActionItem[] = [
    {
      text: 'Copy snippet',
      icon: 'copy',
      onAction: () => copySnippet(entities),
    },
    {
      text: 'Export data',
      icon: 'download',
      onAction: openExportModal,
    },
  ]

  const renderContent = () => {
    if (pageLoading) {
      return (
        <div className={styles.fullPageContainer}>
          <SpinnerLoader ariaLabel={'ConversionAnalyticsDetailsPage'} />
        </div>
      )
    }

    if (!hasStatistic) {
      return (
        <div className={styles.fullPageContainer}>
          <AnalyticsEmptyState />
        </div>
      )
    }

    return (
      <>
        <AnalyticsHeader title={conversionDetail?.name} actions={conversionDetailsActions} />

        <div className={styles.filtersRow}>
          <CalendarDropdown store={calendarFilterStore} />
        </div>
        <div className={styles.kpiSection}>
          <div className={styles.kpiItem}>
            <Typography
              variant={'body-rg-regular'}
              color={'tertiary'}
              ariaLabel={'ConversionRevenueTitle'}
            >
              Conversion Revenue
            </Typography>
            <Typography
              variant={'heading-lg'}
              color={'primary'}
              ariaLabel={'ConversionRevenueValue'}
            >
              ${conversionValueStat?.value?.toLocaleString() || '0'}
            </Typography>
            {conversionValueStat?.trend && (
              <TrendLabel trend={conversionValueStat.trend} displayMode='percent' />
            )}
          </div>
          <div className={styles.kpiDivider}></div>
          <div className={styles.kpiItem}>
            <Typography
              variant={'body-rg-regular'}
              color={'tertiary'}
              ariaLabel={'ConversionsTitle'}
            >
              Conversions
            </Typography>
            <Typography variant={'heading-lg'} color={'primary'} ariaLabel={'ConversionsValue'}>
              {totalStat?.value?.toLocaleString() || '0'}
            </Typography>
            {totalStat?.trend && <TrendLabel trend={totalStat.trend} displayMode='percent' />}
          </div>
          <div className={styles.kpiDivider}></div>
          <div className={styles.kpiItem}>
            <Typography
              variant={'body-rg-regular'}
              color={'tertiary'}
              ariaLabel={'ConversionRateTitle'}
            >
              Conversion rate
            </Typography>
            <Typography variant={'heading-lg'} color={'primary'} ariaLabel={'ConversionRateValue'}>
              {conversionRateStat?.value || '0'}%
            </Typography>
            {conversionRateStat?.trend && (
              <TrendLabel trend={conversionRateStat.trend} displayMode='percent' />
            )}
          </div>
        </div>

        {chartDataStore.hasChartData && (
          <div className={styles.chartSection}>
            <div className={styles.chartHeader}>
              <Typography variant='heading-sm' ariaLabel='ConversionAnalyticsTitle'>
                Conversions
              </Typography>
              <ChartFilter store={chartFilterStore} />
            </div>

            <div className={styles.chartArea}>
              {chartLoading ? (
                <SpinnerLoader ariaLabel={'ConversionAnalyticsDetailsPage'} />
              ) : (
                <>
                  {chartFilterStore.chartType === 'line' ? (
                    <ConversionDetailsLineChart chartDataStore={chartDataStore} />
                  ) : (
                    <ConversionDetailsBarChart chartDataStore={chartDataStore} />
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <FullPageWrapper>
      <Breadcrumbs customPath={`/${AnalyticsPath.conversions}`} />
      {renderContent()}
    </FullPageWrapper>
  )
})

export const ConversionAnalyticsDetailsPage = () => {
  const { id } = useParams<{ id: string }>()
  const conversionId = id ? parseInt(id, 10) : 0

  if (!conversionId) {
    return <NoResultsFound />
  }

  return (
    <ConversionDetailsProvider conversionId={conversionId}>
      <ConversionDetailsPageContent />
    </ConversionDetailsProvider>
  )
}
